import type { FloatingContext, Placement, ReferenceType, UseFloatingReturn } from '@floating-ui/react'
import { createContext, type HTMLProps, type MutableRefObject, type RefCallback, use } from 'react'

import type { DropdownMenuSearch } from './DropdownSearch'

type SubMenuHtmlProps = Omit<HTMLProps<HTMLElement>, 'ref'> & {
    ref?: RefCallback<HTMLElement>
}

type DropdownSubMenuContextValue = {
    open: boolean
    /** The placement Floating UI settled on, which is not the requested one once the panel has flipped. */
    resolvedPlacement: Placement
    context: FloatingContext
    refs: UseFloatingReturn<ReferenceType>['refs']
    /**
     * Position as top/left rather than floating-ui's `floatingStyles`, which positions with a
     * `transform`. The panel animates its scale, and motion writes the whole `transform` property,
     * which would drop the translation and strand the panel in the corner of the viewport.
     */
    x: number
    y: number
    strategy: UseFloatingReturn<ReferenceType>['strategy']
    getReferenceProps: (userProps?: SubMenuHtmlProps) => Record<string, unknown>
    getFloatingProps: (userProps?: SubMenuHtmlProps) => Record<string, unknown>
    getItemProps: (userProps: SubMenuHtmlProps) => SubMenuHtmlProps
    elementsRef: MutableRefObject<Array<HTMLElement | null>>
    labelsRef: MutableRefObject<Array<string | null>>
    /** Closes every menu in the tree, which is what choosing an item in a submenu has to do. */
    closeTree: () => void
    /** The search field the submenu was given, which its content panel renders. */
    search: DropdownMenuSearch | undefined
    /** The row a search field's Enter would choose, kept apart from the focused one floating-ui moves. */
    searchActiveIndex: number | null
    setSearchActiveIndex: (index: number | null) => void
    /**
     * Whether this panel opened as a hand-off from a sibling submenu, which appears as the pointer moving
     * down a column of triggers. Animating each one in turn reads as flicker, so the hand-off is instant.
     */
    openedAsHandoff: boolean
}

export const DropdownSubMenuContext = createContext<DropdownSubMenuContextValue | null>(null)

export function useDropdownSubMenuContext() {
    const context = use(DropdownSubMenuContext)
    if (!context) throw new Error('AstraDropdownMenuSubTrigger and AstraDropdownMenuSubContent must be rendered inside an AstraDropdownMenuSub')
    return context
}
